import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, PenLine, FileText, BellRing, Bot, Globe2, ShieldCheck, Check, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Logo, ThemeToggle, Footer, Eyebrow, StatusBadge } from "@/components/sg/Brand";
import { PlanCards, PRICING_FALLBACK } from "@/components/sg/PlanCards";
import { FaqList } from "@/components/sg/FaqList";
import { VideoReel } from "@/components/sg/VideoReel";

function Nav() {
  const { user } = useAuth();
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        <Logo />
        <nav className="flex items-center gap-1 sm:gap-3 text-sm">
          <a href="#pricing" className="hidden sm:inline px-2 text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-pricing-link">Pricing</a>
          <Link to="/faq" className="hidden sm:inline px-2 text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-faq-link">FAQ</Link>
          <ThemeToggle />
          {user ? (
            <Link to="/app"><Button data-testid="nav-open-app-btn" className="bg-indigo-600 hover:bg-indigo-500 text-white">Open workspace</Button></Link>
          ) : (
            <>
              <Link to="/auth" className="px-2 text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-signin-link">Sign in</Link>
              <Link to="/auth?mode=register"><Button data-testid="nav-start-free-btn" className="bg-indigo-600 hover:bg-indigo-500 text-white">Start free</Button></Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

function HeroMock() {
  const rows = [
    { name: "Down payment", amount: "$1,500.00", status: "paid" },
    { name: "Drywall installed", amount: "$2,000.00", status: "ready_to_invoice" },
    { name: "Cabinets & handles", amount: "$1,500.00", status: "awaiting_signature" },
  ];
  return (
    <div className="border border-border rounded-xl bg-card p-6 relative" data-testid="hero-mock">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Client</div>
          <div className="font-display text-xl font-bold">Sarah's kitchen remodel</div>
        </div>
        <div className="font-display text-2xl font-bold text-indigo-600 dark:text-indigo-400">$5,000</div>
      </div>
      <ol className="mt-6 border-l border-border ml-2 space-y-5">
        {rows.map((r) => (
          <li key={r.name} className="ml-5 flex items-center justify-between gap-3">
            <div className="font-medium">{r.name}</div>
            <div className="text-right"><div className="font-display font-bold tabular-nums">{r.amount}</div><StatusBadge status={r.status} /></div>
          </li>
        ))}
      </ol>
      <div className="mt-6 border-t border-border pt-4 text-xs text-muted-foreground flex items-center gap-2">
        <Clock className="h-3.5 w-3.5" /> Auto-reminder scheduled in 24h for "Cabinets & handles"
      </div>
    </div>
  );
}

const STEPS = [
  { icon: Sparkles, title: "Describe the job", text: "Type it like you'd text a friend. AI builds a milestone plan with amounts that add up." },
  { icon: PenLine, title: "Client signs each step", text: "One secure link. No app, no account. They type their name and approve in seconds." },
  { icon: FileText, title: "Invoice & get paid", text: "Download a branded PDF invoice with the signature attached. Mark paid when it lands." },
];

const FEATURES = [
  { icon: ShieldCheck, title: "Scope protection", text: "Every phase is signed and time-stamped, so “I never agreed to that” never costs you." },
  { icon: BellRing, title: "Hands-off follow-ups", text: "Automatic email reminders at 24h and 48h, SMS on Super Premium, and overdue alerts for you." },
  { icon: FileText, title: "One-click invoices", text: "Professional PDF invoices with your company name, ready to send or file for taxes." },
  { icon: Bot, title: "AI business copilot", text: "Price a job, write a change order, or draft a firm-but-friendly payment nudge." },
  { icon: Globe2, title: "Portfolio that sells", text: "A public page of your signed-off projects with an inquiry form that feeds your lead inbox." },
  { icon: Sparkles, title: "Built for phones", text: "Clients approve from any phone. You run everything from the job site." },
];

export default function Landing() {
  const nav = useNavigate();
  const [pricing, setPricing] = useState(PRICING_FALLBACK);
  useEffect(() => {
    api.get("/payments/pricing").then((r) => setPricing(r.data)).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Nav />
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-20 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7">
          <Eyebrow>For independent contractors</Eyebrow>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-4 leading-[1.05]">
            Get every milestone signed.<br />Get paid on time.
          </h1>
          <p className="text-base md:text-lg text-muted-foreground mt-6 max-w-xl leading-relaxed">
            ScopeGuard turns your job description into a signed, step-by-step payment plan — then chases approvals and builds invoices for you.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Button data-testid="hero-start-free-btn" onClick={() => nav("/auth?mode=register")} className="h-12 px-6 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold">
              Try Super Premium free for 7 days <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <Button data-testid="hero-faq-btn" variant="outline" onClick={() => document.getElementById("videos")?.scrollIntoView({ behavior: "smooth" })} className="h-12 px-6">Watch how it works</Button>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-8 text-sm text-muted-foreground">
            {["Set up in 2 minutes", "Clients need no account", "Free plan forever, cancel any time"].map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />{t}</span>
            ))}
          </div>
        </div>
        <div className="md:col-span-5"><HeroMock /></div>
      </section>

      <section id="videos" className="max-w-7xl mx-auto px-6 pb-20 scroll-mt-20" data-testid="videos-section">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <Eyebrow>See it in 15 seconds</Eyebrow>
            <h2 className="font-display text-3xl font-bold mt-3 max-w-xl">Three problems every contractor knows. One app that fixes them.</h2>
          </div>
          <p className="text-sm text-muted-foreground max-w-sm">Real ScopeGuard screens, start to finish. Tap the speaker for sound.</p>
        </div>
        <VideoReel />
      </section>

      <section className="border-y border-border bg-muted/40">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <Eyebrow>How it works</Eyebrow>
          <div className="grid md:grid-cols-3 gap-8 mt-6">
            {STEPS.map((s, i) => (
              <div key={s.title} data-testid={`how-step-${i}`}>
                <div className="flex items-center gap-3"><span className="font-display text-sm font-bold text-muted-foreground">0{i + 1}</span><s.icon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" /></div>
                <h3 className="font-display text-lg font-bold mt-3">{s.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <Eyebrow>Why contractors switch</Eyebrow>
        <h2 className="font-display text-3xl font-bold mt-3 max-w-2xl">Everything between “you're hired” and “paid in full”.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border rounded-xl overflow-hidden mt-10">
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-card p-6">
              <f.icon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              <h3 className="font-display font-bold mt-4">{f.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="pricing" className="max-w-7xl mx-auto px-6 py-16 scroll-mt-20">
        <Eyebrow>Pricing</Eyebrow>
        <h2 className="font-display text-3xl font-bold mt-3 mb-8">Less than one late payment costs you.</h2>
        <PlanCards pricing={pricing} showFree trial onSelect={() => nav("/auth?mode=register")} />
      </section>

      <section className="max-w-3xl mx-auto px-6 py-16">
        <Eyebrow>FAQ</Eyebrow>
        <h2 className="font-display text-3xl font-bold mt-3 mb-8">Questions, answered.</h2>
        <FaqList limit={6} />
        <Link to="/faq" className="inline-block mt-4 text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:underline" data-testid="landing-all-faq-link">See all questions & chat with support →</Link>
      </section>

      <section className="max-w-7xl mx-auto px-6">
        <div className="rounded-xl bg-slate-900 dark:bg-indigo-600 text-white p-10 md:p-14 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-3xl font-bold">Your next job deserves a signed plan.</h2>
            <p className="text-slate-300 dark:text-indigo-100 mt-2">Start with 7 days of Super Premium, or stay free for your first 2 projects.</p>
          </div>
          <Button data-testid="cta-band-start-btn" onClick={() => nav("/auth?mode=register")} className="h-12 px-6 bg-white text-slate-900 hover:bg-slate-100 font-semibold">Start my free trial</Button>
        </div>
      </section>
      <Footer />
    </div>
  );
}
